import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";
import { authFetch } from "@/lib/authFetch";

export interface AddressResult {
  label: string;
  voie: string;
  code_postal: string;
  ville: string;
  departement: string | null;
  latitude: number | null;
  longitude: number | null;
  score: number | null;
}

interface Props {
  value?: string;
  onChange?: (value: string) => void;
  onSelect: (result: AddressResult) => void;
  placeholder?: string;
  required?: boolean;
  "data-testid"?: string;
}

export default function AddressAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder = "Commencez à taper l'adresse…",
  required,
  "data-testid": testId,
}: Props) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function search(text: string) {
    setQuery(text);
    onChange?.(text);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (text.trim().length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      try {
        const r = await authFetch(`/api/geo/autocomplete?q=${encodeURIComponent(text)}`);
        if (!r.ok) { setResults([]); return; }
        const data = await r.json();
        const sugg = data.suggestions ?? [];
        setResults(sugg);
        setOpen(sugg.length > 0);
        setActive(-1);
      } catch {
        setResults([]);
      }
    }, 250);
  }

  function pick(s: any) {
    const result: AddressResult = {
      label: s.label,
      voie: s.adresse ?? s.label,
      code_postal: s.codePostal ?? "",
      ville: s.ville ?? "",
      departement: s.departement ?? null,
      latitude: s.latitude ?? null,
      longitude: s.longitude ?? null,
      score: s.score ?? null,
    };
    setQuery(s.label);
    setOpen(false);
    setResults([]);
    onSelect(result);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <Input
        value={query}
        onChange={(e) => search(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        data-testid={testId}
      />
      {open && results.length > 0 && (
        <ul
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "var(--background, #fff)",
            border: "1px solid hsl(var(--border))",
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(0,0,0,.12)",
            margin: 0,
            padding: "4px",
            listStyle: "none",
            maxHeight: "260px",
            overflowY: "auto",
          }}
        >
          {results.map((s, i) => {
            const isActive = i === active;
            return (
              <li
                key={i}
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                onMouseEnter={() => setActive(i)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 10px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  background: isActive ? "hsl(var(--accent))" : "transparent",
                  color: isActive ? "hsl(var(--accent-foreground))" : "inherit",
                  fontSize: "13px",
                  lineHeight: "1.3",
                }}
              >
                <MapPin
                  style={{
                    width: 13,
                    height: 13,
                    flexShrink: 0,
                    color: "hsl(var(--muted-foreground))",
                  }}
                />
                <span>{s.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
